import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// confirmDialog function
export function confirmDialog({
  title,
  message,
  confirmText = 'Yes',
  cancelText = 'No',
  variant = 'standard',
  onBackdropClick,
}) {
  return new Promise((resolve) => {
    window.dispatchEvent(
      new CustomEvent('open-confirm-dialog', {
        detail: { title, message, confirmText, cancelText, variant, resolve, onBackdropClick },
      }),
    );
  });
}

// Provider Component (global dialog listener)
export default function ConfirmationProvider() {
  const [open, setOpen] = useState(false);
  const [ui, setUi] = useState({
    title: '',
    message: '',
    confirmText: 'Yes',
    cancelText: 'No',
    variant: 'standard',
    onBackdropClick: false,
  });

  const resolveRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {};

      setUi({
        title: detail.title || '',
        message: detail.message || '',
        confirmText: detail.confirmText || 'Yes',
        cancelText: detail.cancelText || 'No',
        variant: detail.variant || 'standard',
        onBackdropClick: detail.onBackdropClick || false,
      });

      resolveRef.current = detail.resolve;
      setOpen(true);
    };

    window.addEventListener('open-confirm-dialog', handler);
    return () => window.removeEventListener('open-confirm-dialog', handler);
  }, []);

  const handleClose = (result) => {
    setOpen(false);
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  };

  const isDestructive = ui.variant === 'destructive';

  return (
    <Dialog
      open={open}
      onClose={() => (ui.onBackdropClick ? handleClose('outsideClick') : handleClose(false))}
      {...(isDestructive
        ? {
            fullWidth: true,
            maxWidth: 'xs',
            slotProps: {
              paper: {
                sx: {
                  borderRadius: 2,
                  overflow: 'hidden',
                },
              },
            },
          }
        : {})}
    >
      {isDestructive ? (
        <>
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              px: 3.5,
              py: 2.5,
            }}
          >
            {ui.title}
            <IconButton
              size='small'
              onClick={() => handleClose(false)}
              aria-label='Close confirmation'
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ px: 3.5, pt: 0, pb: 2.5 }}>
            <DialogContentText sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              {ui.message}
            </DialogContentText>
          </DialogContent>

          <DialogActions sx={{ bgcolor: 'background.default', px: 3.5, py: 2, gap: 1.5 }}>
            <Button
              onClick={() => handleClose(false)}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              {ui.cancelText}
            </Button>
            <Button
              variant='contained'
              color='error'
              onClick={() => handleClose(true)}
              autoFocus
              sx={{ borderRadius: '24px', px: 3, textTransform: 'none', fontWeight: 800 }}
            >
              {ui.confirmText}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>{ui.title}</DialogTitle>

          <DialogContent>
            <DialogContentText>{ui.message}</DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button variant='contained' color='primary' onClick={() => handleClose(true)} autoFocus>
              {ui.confirmText}
            </Button>

            <Button color='inherit' onClick={() => handleClose(false)}>
              {ui.cancelText}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
